import React from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";

export const SKIP_WEEKDAY_CONDITIONS_DEFAULT = {
  enabled: false,
  skip_weekday: "monday",
  scope: "next_occurrence",
  channels: ["site_visit"],
  apply_on_emergency: false,
  when_client_writes: [],
};

const WEEKDAY_ROWS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

const CHANNEL_ROWS = [
  { key: "site_visit", label: "Site visit" },
  { key: "phone_consult", label: "Phone consult" },
];

const FIELD_CLASS =
  "w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body";

const HINT_CLASS = "text-amber-400/80 text-xs font-body";

const BLANK_WINDOW = { days: [], start: "00:00", end: "23:59" };

function asObject(value) {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
}

function ToggleRow({ label, description, checked, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-all">
      <div className="flex-1">
        <span className="text-[#F3F4F6] text-sm font-body">{label}</span>
        {description && (
          <p className="text-[#9CA3AF] text-xs font-body mt-1">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative w-12 h-6 rounded-full transition-colors duration-300 flex-shrink-0 ${
          checked ? "bg-[#00D1FF]" : "bg-white/20"
        }`}
        aria-pressed={checked}
      >
        <div
          className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-300 ${
            checked ? "translate-x-7" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
}

function toggleInList(list, key) {
  return list.includes(key)
    ? list.filter((item) => item !== key)
    : [...list, key];
}

/**
 * @param {{
 *   value?: object,
 *   onChange?: (next: object) => void,
 *   showEmergencyOption?: boolean,
 *   ghlIntegrationActive?: boolean,
 * }} props
 */
export function SkipWeekdayConditionsEditor({
  value,
  onChange,
  showEmergencyOption = false,
  ghlIntegrationActive = false,
}) {
  const cfg = { ...SKIP_WEEKDAY_CONDITIONS_DEFAULT, ...asObject(value) };
  const windows = Array.isArray(cfg.when_client_writes)
    ? cfg.when_client_writes
    : [];
  const channels = Array.isArray(cfg.channels) ? cfg.channels : [];

  const emit = (overrides) => {
    if (typeof onChange !== "function") return;
    onChange({ ...cfg, ...overrides });
  };

  const updateWindow = (idx, patch) => {
    emit({
      when_client_writes: windows.map((row, i) => {
        if (i !== idx) return row;
        return { ...asObject(row), ...patch };
      }),
    });
  };

  return (
    <div className="space-y-4" data-testid="skip-weekday-conditions-editor">
      {!ghlIntegrationActive && (
        <p className="text-amber-400/80 text-xs font-body leading-relaxed">
          Connect{" "}
          <Link
            to="/dashboard/integrations/crm/ghl"
            className="text-[#00D1FF] hover:underline"
          >
            GoHighLevel
          </Link>{" "}
          or{" "}
          <Link
            to="/dashboard/integrations/calendars/google"
            className="text-[#00D1FF] hover:underline"
          >
            Google Calendar
          </Link>{" "}
          for this rule to take effect. Without a connected calendar the bot
          offers times from its own operating-hours logic, which this rule does
          not filter.
        </p>
      )}

      <p className="text-[#9CA3AF] text-xs font-body leading-relaxed">
        If the client writes during these windows, the selected weekday is
        hidden from the offered appointment slots. Times use the office
        timezone.
      </p>

      <ToggleRow
        label="Enable skip weekday conditions"
        checked={!!cfg.enabled}
        onChange={(next) => emit({ enabled: next })}
      />

      <div className="space-y-1">
        <label className="text-[#F3F4F6] text-sm font-body">
          Weekday to skip
        </label>
        <select
          className={FIELD_CLASS}
          value={cfg.skip_weekday}
          onChange={(e) => emit({ skip_weekday: e.target.value })}
        >
          {WEEKDAY_ROWS.map(({ key, label }) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <p className="text-[#F3F4F6] text-sm font-body">Scope</p>
        {[
          {
            value: "next_occurrence",
            label: "Only the next occurrence",
          },
          {
            value: "all_matching_weekdays",
            label: "Every matching weekday",
          },
        ].map((option) => (
          <label
            key={option.value}
            className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              cfg.scope === option.value
                ? "bg-[#00D1FF]/10 border-[#00D1FF]/50"
                : "bg-white/5 border-white/10 hover:border-white/20"
            }`}
          >
            <input
              type="radio"
              name="skip-weekday-scope"
              className="mt-1 accent-[#00D1FF]"
              checked={cfg.scope === option.value}
              onChange={() => emit({ scope: option.value })}
            />
            <span className="text-[#F3F4F6] text-sm font-body">
              {option.label}
            </span>
          </label>
        ))}
      </div>

      <div className="space-y-2">
        <p className="text-[#F3F4F6] text-sm font-body">Channels</p>
        {CHANNEL_ROWS.map(({ key, label }) => (
          <label
            key={key}
            className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer"
          >
            <input
              type="checkbox"
              className="accent-[#00D1FF]"
              checked={channels.includes(key)}
              onChange={() => emit({ channels: toggleInList(channels, key) })}
            />
            <span className="text-[#F3F4F6] text-sm font-body">{label}</span>
          </label>
        ))}
        {channels.length === 0 && (
          <p className={HINT_CLASS}>
            select at least one channel, otherwise nothing is skipped
          </p>
        )}
      </div>

      {showEmergencyOption && (
        <ToggleRow
          label="Still apply during emergency dispatch"
          checked={!!cfg.apply_on_emergency}
          onChange={(next) => emit({ apply_on_emergency: next })}
        />
      )}

      <div className="space-y-3">
        <p className="text-[#F3F4F6] text-sm font-body">When the client writes</p>
        {cfg.enabled && windows.length === 0 && (
          <p className={HINT_CLASS}>
            add at least one window, otherwise nothing is skipped
          </p>
        )}
        {windows.map((raw, idx) => {
          const row = asObject(raw);
          const days = Array.isArray(row.days) ? row.days : [];
          const start = String(row.start || "");
          const end = String(row.end || "");
          const overnight = Boolean(start && end && start > end);
          const neverMatches = days.length === 0;
          return (
            <div
              key={idx}
              className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[#00D1FF] text-xs">
                  Window {idx + 1}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    emit({
                      when_client_writes: windows.filter((_, i) => i !== idx),
                    })
                  }
                  className="text-[#9CA3AF] hover:text-red-400 transition-colors p-1"
                  aria-label="Remove window"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-3">
                {WEEKDAY_ROWS.map(({ key, label }) => (
                  <label
                    key={key}
                    className="flex items-center gap-2 text-[#F3F4F6] text-xs font-body cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="accent-[#00D1FF]"
                      checked={days.includes(key)}
                      onChange={() =>
                        updateWindow(idx, { days: toggleInList(days, key) })
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-[#9CA3AF] text-xs font-body">Start</span>
                  <input
                    type="time"
                    className={FIELD_CLASS}
                    value={start}
                    onChange={(e) => updateWindow(idx, { start: e.target.value })}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-[#9CA3AF] text-xs font-body">End</span>
                  <input
                    type="time"
                    className={FIELD_CLASS}
                    value={end}
                    onChange={(e) => updateWindow(idx, { end: e.target.value })}
                  />
                </label>
              </div>
              {overnight && (
                <p className={HINT_CLASS}>
                  this window is ignored — overnight ranges need two windows
                </p>
              )}
              {neverMatches && (
                <p className={HINT_CLASS}>
                  this window will never match
                </p>
              )}
            </div>
          );
        })}
        <button
          type="button"
          onClick={() =>
            emit({ when_client_writes: [...windows, { ...BLANK_WINDOW }] })
          }
          className="flex items-center gap-2 text-[#00D1FF] text-sm font-body hover:text-[#00D1FF]/80 transition-colors py-2"
        >
          <Plus className="w-4 h-4" />
          Add window
        </button>
      </div>
    </div>
  );
}
